import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  FileSpreadsheet,
  Loader2,
  ShieldCheck,
  UploadCloud,
} from 'lucide-react';

import {
  useCreateDatabaseConnector,
  useUploadFileConnector,
} from '@/features/connectors/hooks';
import type { ConnectorType, DatabaseDriver } from '@/features/connectors/types';
import { DB_ENGINES, defaultPort } from '@/features/connectors/engines';
import { isApiError } from '@/lib/errors';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

const inputClass =
  'h-10 w-full rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 px-3 text-sm text-ink-900 dark:text-ink-50 placeholder:text-ink-400 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100';

export function AddConnectorPage(): JSX.Element {
  const [tab, setTab] = useState<ConnectorType>('database');

  return (
    <div className="w-full space-y-6">
      <Link
        to="/connectors"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 dark:text-ink-400 transition hover:text-brand-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to data sources
      </Link>

      <div>
        <div className="eyebrow eyebrow-dot mb-2.5">
          <span>SETUP</span>
        </div>
        <h1 className="h-display">Add connector</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-500 dark:text-ink-400">
          Choose how you want to bring data in. Credentials are stored encrypted; raw data never
          leaves your perimeter.
        </p>
      </div>

      {/* Segmented tabs */}
      <div className="grid grid-cols-2 gap-2 rounded-xl border border-ink-200/70 dark:border-white/10 bg-ink-50/60 dark:bg-white/[0.03] p-1.5 shadow-soft">
        <TabButton
          active={tab === 'database'}
          onClick={() => setTab('database')}
          icon={Database}
          title="Database"
          description="MySQL or PostgreSQL"
        />
        <TabButton
          active={tab === 'file'}
          onClick={() => setTab('file')}
          icon={FileSpreadsheet}
          title="File upload"
          description="CSV or XLSX"
        />
      </div>

      <div className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-6 shadow-soft animate-slide-up">
        {tab === 'database' ? <DatabaseForm /> : <FileForm />}
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-brand-100 dark:border-brand-800/40 bg-brand-50/50 p-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="text-xs text-ink-700 dark:text-ink-200">
          <div className="font-semibold text-ink-900 dark:text-ink-50">Secure by default</div>
          Use a read-only role · credentials encrypted at rest · TLS recommended for remote
          databases.
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  title,
  description,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Database;
  title: string;
  description: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${
        active
          ? 'bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-50 shadow-soft ring-1 ring-ink-200/70'
          : 'text-ink-600 dark:text-ink-300 hover:bg-white/60 dark:hover:bg-white/5'
      }`}
    >
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg transition ${
          active
            ? 'bg-gradient-to-br from-brand-400 to-brand-700 text-white shadow-glow'
            : 'bg-ink-100 dark:bg-white/[0.06] text-ink-500 dark:text-ink-400 group-hover:bg-ink-200'
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="leading-tight">
        <div
          className={`font-display text-sm font-semibold ${active ? 'text-ink-900 dark:text-ink-50' : 'text-ink-700 dark:text-ink-200'}`}
        >
          {title}
        </div>
        <div className="font-mono text-[10px] tracking-wider text-ink-500 dark:text-ink-400">{description}</div>
      </div>
    </button>
  );
}

function DatabaseForm(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateDatabaseConnector();
  const [name, setName] = useState('');
  const [driver, setDriver] = useState<DatabaseDriver>('mysql');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(3306);
  const [database, setDatabase] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const connector = await create
      .mutateAsync({ name, driver, host, port, database, username, password })
      .catch(() => null);
    if (connector) {
      navigate(`/connectors/${connector.id}`);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="Connector name">
        <input
          className={inputClass}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. analytics_warehouse"
        />
      </Field>

      <Field label="Engine">
        <div className="relative">
          <SearchableSelect
            value={driver}
            onChange={(val) => {
              const next = val as DatabaseDriver;
              setDriver(next);
              setPort(defaultPort(next));
            }}
            options={DB_ENGINES.map((e) => ({ label: e.label, value: e.value }))}
          />
        </div>
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Host">
            <input
              className={`${inputClass} font-mono`}
              required
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="db.internal"
            />
          </Field>
        </div>
        <Field label="Port">
          <input
            type="number"
            className={`${inputClass} font-mono`}
            required
            value={port}
            onChange={(e) => setPort(Number(e.target.value))}
          />
        </Field>
      </div>

      <Field label="Database">
        <input
          className={`${inputClass} font-mono`}
          required
          value={database}
          onChange={(e) => setDatabase(e.target.value)}
          placeholder="analytics"
        />
      </Field>

      <Field label="Username">
        <input
          className={`${inputClass} font-mono`}
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="nlytics_ro"
        />
      </Field>

      <Field label="Password">
        <input
          type="password"
          className={inputClass}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="off"
          placeholder="••••••••"
        />
      </Field>

      {create.isError && (
        <p className="rounded-md border border-accent-crimson/25 bg-accent-crimson/[0.06] px-3 py-2 text-xs font-medium text-accent-crimson">
          {isApiError(create.error) ? create.error.message : 'Failed to create connector.'}
        </p>
      )}

      <button
        type="submit"
        disabled={create.isPending}
        className="btn-primary h-11 w-full text-sm"
      >
        {create.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Creating…
          </>
        ) : (
          'Create connector'
        )}
      </button>
    </form>
  );
}

function FileForm(): JSX.Element {
  const navigate = useNavigate();
  const upload = useUploadFileConnector();
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!file) {
      return;
    }
    const connector = await upload.mutateAsync({ name, file }).catch(() => null);
    if (connector) {
      navigate(`/connectors/${connector.id}`);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="Dataset name">
        <input
          className={inputClass}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. customer_extract_2026_q1"
        />
      </Field>

      <Field label="File (CSV or XLSX)">
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) setFile(f);
          }}
          className={`block cursor-pointer rounded-xl border-2 border-dashed bg-white dark:bg-ink-900 p-6 text-center transition ${
            dragging
              ? 'border-brand-400 bg-brand-50/60 dark:bg-brand-900/25'
              : file
                ? 'border-brand-300 bg-brand-50/30 dark:bg-brand-900/20'
                : 'border-ink-300 dark:border-white/15 hover:border-ink-400'
          }`}
        >
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600">
            <UploadCloud className="h-5 w-5" />
          </div>
          {file ? (
            <>
              <div className="mt-3 truncate font-mono text-sm font-semibold text-ink-900 dark:text-ink-50">
                {file.name}
              </div>
              <div className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
                {(file.size / 1024).toFixed(1)} KB · ready to upload
              </div>
            </>
          ) : (
            <>
              <div className="mt-3 text-sm font-semibold text-ink-900 dark:text-ink-50">
                Drop a file here, or click to browse
              </div>
              <div className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">CSV, TXT, or XLSX</div>
            </>
          )}
          <input
            type="file"
            accept=".csv,.txt,.xlsx"
            required
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="hidden"
          />
        </label>
      </Field>

      {upload.isError && (
        <p className="rounded-md border border-accent-crimson/25 bg-accent-crimson/[0.06] px-3 py-2 text-xs font-medium text-accent-crimson">
          {isApiError(upload.error) ? upload.error.message : 'Upload failed.'}
        </p>
      )}

      <button
        type="submit"
        disabled={upload.isPending || !file}
        className="btn-primary h-11 w-full text-sm"
      >
        {upload.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Uploading & parsing…
          </>
        ) : (
          'Upload & ingest'
        )}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="block space-y-1.5">
      <span className="text-xs font-medium text-ink-700 dark:text-ink-200">{label}</span>
      {children}
    </div>
  );
}
